import React, { useState } from "react";
import { ExerciseEditorProps } from "./exercise-editor-types";
import { ConversationalExerciseContent } from "@org/ui";
import { Label } from "@org/ui";
import { TextArea } from "@org/ui";
import { Input } from "@org/ui";
import { XIcon } from "lucide-react";

export const ConversationalExerciseEditor: React.FC<ExerciseEditorProps<ConversationalExerciseContent>> = ({
  content,
  updateContent
}) => {
  const [skillInput, setSkillInput] = useState("");

  const handleUpdate = (nextData: Partial<ConversationalExerciseContent["data"]>) => {
    updateContent({
      ...content,
      data: {
        ...content.data,
        ...nextData
      }
    });
  };

  const commitSkills = (rawValue: string) => {
    const newSkills = rawValue
      .split(",")
      .map(s => s.trim())
      .filter(s => s.length > 0);
    if (newSkills.length === 0) return;

    const existing = content.data.targetSkills ?? [];
    const merged = [...existing];
    for (const skill of newSkills) {
      if (!merged.some(s => s.toLowerCase() === skill.toLowerCase())) {
        merged.push(skill);
      }
    }
    handleUpdate({ targetSkills: merged });
    setSkillInput("");
  };

  const handleSkillsChange = (value: string) => {
    // If value contains a comma, commit all complete skills and keep the trailing part
    if (value.includes(",")) {
      const parts = value.split(",");
      const trailing = parts.pop()!; // last segment (after final comma) stays in input
      const toAdd = parts.map(s => s.trim()).filter(s => s.length > 0);
      if (toAdd.length > 0) {
        const existing = content.data.targetSkills ?? [];
        const merged = [...existing];
        for (const skill of toAdd) {
          if (!merged.some(s => s.toLowerCase() === skill.toLowerCase())) {
            merged.push(skill);
          }
        }
        handleUpdate({ targetSkills: merged });
      }
      setSkillInput(trailing);
    } else {
      setSkillInput(value);
    }
  };

  const handleSkillKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitSkills(skillInput);
    }
  };

  const handleSkillBlur = () => {
    if (skillInput.trim()) {
      commitSkills(skillInput);
    }
  };

  const handleRemoveSkill = (indexToRemove: number) => {
    const updated = (content.data.targetSkills ?? []).filter((_, i) => i !== indexToRemove);
    handleUpdate({ targetSkills: updated });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`theme-${content.id}`}>Conversation Theme</Label>
        <Input
          id={`theme-${content.id}`}
          value={content.data.theme}
          onChange={(event) => handleUpdate({ theme: event.target.value })}
          placeholder="e.g., Ordering food at a restaurant"
          className="mt-1"
        />
        <p className="text-xs text-muted-foreground mt-1">
          The topic or scenario for the AI conversation
        </p>
      </div>

      <div>
        <Label htmlFor={`duration-${content.id}`}>Maximum Duration (seconds)</Label>
        <Input
          id={`duration-${content.id}`}
          type="number"
          value={content.data.maxDurationSec}
          min={60}
          max={900}
          onChange={(event) => handleUpdate({ maxDurationSec: parseInt(event.target.value, 10) || 300 })}
          className="mt-1"
        />
        <p className="text-xs text-muted-foreground mt-1">
          How long the conversation can last (60-900 seconds)
        </p>
      </div>

      <div>
        <Label htmlFor={`skills-${content.id}`}>Target Skills</Label>
        <Input
          id={`skills-${content.id}`}
          value={skillInput}
          onChange={(event) => handleSkillsChange(event.target.value)}
          onKeyDown={handleSkillKeyDown}
          onBlur={handleSkillBlur}
          placeholder="Type a skill and press Enter"
          className="mt-1"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Press Enter or use commas to add multiple skills
        </p>
        {(content.data.targetSkills ?? []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {(content.data.targetSkills ?? []).map((skill, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
                {skill}
                <button
                  type="button"
                  className="ml-1 text-slate-400 hover:text-red-500"
                  onClick={() => handleRemoveSkill(idx)}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor={`system-prompt-${content.id}`}>Custom AI Instructions (Optional)</Label>
        <TextArea
          id={`system-prompt-${content.id}`}
          value={content.data.systemPrompt ?? ""}
          onChange={(event) => handleUpdate({ systemPrompt: event.target.value })}
          placeholder="Additional instructions for the AI tutor behavior..."
          className="mt-1"
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Customize how the AI should behave during the conversation
        </p>
      </div>

      <div>
        <Label htmlFor={`correction-${content.id}`}>Evaluation Notes</Label>
        <TextArea
          id={`correction-${content.id}`}
          value={content.data.correction}
          onChange={(event) => handleUpdate({ correction: event.target.value })}
          placeholder="Notes for evaluating this conversation..."
          className="mt-1"
        />
      </div>
    </div>
  );
};











